/**
 * ============================================
 * SKYLIT GIFT CARD - 3D CARD (gift-card-3d)
 *
 * Drives snippets/gift-card-3d.liquid: drag or swipe to spin the card on
 * both axes (sideways and end over end), tap or "Flip card" to turn it,
 * a slow idle sway when nobody is touching it, and a foil sheen that
 * slides with the rotation. Pure CSS 3D on the .gc3d__card element; this
 * file only writes CSS variables and a few state classes.
 *
 * Motion model: while dragging, the angles follow the pointer. On release
 * a critically damped spring carries the card on in the direction of the
 * flick and lands it on the next face-aligned angle, so it never turns
 * back on itself. At rest it sways and leans towards the pointer.
 *
 * Faces and edges are shown/hidden from the angle (gc3d--back, gc3d--edge-*)
 * because WebKit does not sort the 3D layers reliably; see the CSS.
 *
 * Light: a glare and a holographic foil follow the pointer, the finger, or
 * (opt-in on phones) the device tilt, in the spirit of simeydotme's
 * pokemon-cards-css.
 *
 * Public: element.setValue('£50') swaps the printed value with a pop.
 *
 * Copyright (c) 2026 Skylit Studio
 * ============================================
 */
(function () {
  'use strict';

  if (customElements.get('gift-card-3d')) return;

  var REST_RX = -8;          // resting lean, degrees
  var REST_RY = -18;
  var DRAG_SENS = 0.55;      // degrees per pixel dragged sideways
  var TILT_SENS = 0.45;      // degrees per pixel dragged up / down
  var FRICTION = 0.955;      // only used to predict where a flick would coast to
  var TAP_MAX_PX = 8;
  var TAP_MAX_MS = 320;
  var MAX_V_MOUSE = 26;      // deg per frame
  var MAX_V_TOUCH = 16;
  var K_FLIP = 0.012;        // spring stiffness for a deliberate flip
  var K_FLICK = 0.0045;      // spring stiffness after a flick
  var K_NUDGE = 0.02;        // wobble after a value change
  var DEG = Math.PI / 180;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function mod(a, n) { return ((a % n) + n) % n; }
  // Nearest angle of the form k * 180 + offset to `angle`.
  function nearestFaceAngle(angle, offset) { return Math.round((angle - offset) / 180) * 180 + offset; }

  class GiftCard3D extends HTMLElement {}

  GiftCard3D.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;

    this.card = this.querySelector('.gc3d__card');
    this.valueEl = this.querySelector('[data-gc-value]');
    this.flipBtn = this.querySelector('[data-gc-flip]');
    this.tiltBtn = this.querySelector('[data-gc-tilt]');
    if (!this.card) return;
    this.corners = Array.prototype.map.call(this.querySelectorAll('.gc3d__corner'), function (el) {
      return { el: el, nx: +el.dataset.nx, ny: +el.dataset.ny, on: false };
    });
    // The moving parts. Their styles are written directly: a custom property
    // on the host would re-resolve styles for every star in the SVG on each
    // frame (measured ~10ms/frame), a leaf's inline style touches only itself.
    this.sheens = Array.prototype.slice.call(this.querySelectorAll('.gc3d__sheen'));
    this.glares = Array.prototype.slice.call(this.querySelectorAll('.gc3d__glare'));
    this.foil = this.querySelector('.gc3d__foil');
    this.foilLight = this.querySelector('.gc3d__foil-light');

    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.rx = REST_RX;
    this.ry = REST_RY;
    this.vx = 0;
    this.vy = 0;
    this.mode = 'rest';                 // 'rest' | 'drag' | 'spring'
    this.restRx = REST_RX;              // where the card sits when idle
    this.restRy = REST_RY;
    this.tx = REST_RX; this.ty = REST_RY; this.k = K_FLIP;
    this.dragging = false;
    this.visible = true;
    this.hoverX = 0;
    this.hoverY = 0;
    this.gx = 0.5; this.gy = 0.5;       // where the light should be (0..1 on the face)
    this.cgx = 0.5; this.cgy = 0.5;     // where it is right now (eased)
    this.glareT = 0; this.glare = 0;    // glare strength target / current
    this.t0 = performance.now();
    this.last = {};

    this.bindPointer();
    this.bindHover();
    this.bindTilt();
    this.bindHaptics();
    if (this.flipBtn) {
      var self0 = this;
      this.flipBtn.addEventListener('click', function () { self0.haptic('on'); self0.flip(); });
    }

    var self = this;
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.running = false;
      else if (self.visible) self.start();
    });
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) self.start();
      }, { threshold: 0.05 });
      this.io.observe(this);
    }
    this.start();
  };

  GiftCard3D.prototype.disconnectedCallback = function () {
    if (this.io) this.io.disconnect();
    this.running = false;
  };

  // Light target from a client point, relative to the card's box.
  GiftCard3D.prototype.lightAt = function (clientX, clientY, strength) {
    var r = this.card.getBoundingClientRect();
    if (!r.width || !r.height) return;
    this.gx = clamp((clientX - r.left) / r.width, 0, 1);
    this.gy = clamp((clientY - r.top) / r.height, 0, 1);
    this.glareT = strength;
  };
  GiftCard3D.prototype.lightOff = function () {
    if (this.tilting) return;
    this.glareT = 0;
    this.gx = 0.5; this.gy = 0.5;
  };

  // ---- spring towards a face-aligned pose ----
  GiftCard3D.prototype.springTo = function (tx, ty, k) {
    this.tx = tx;
    this.ty = ty;
    this.k = k;
    this.mode = 'spring';
    // A tick on landing only when the card actually travels somewhere.
    this.landed = Math.abs(tx - this.rx) > 20 || Math.abs(ty - this.ry) > 20;
  };

  // Where a flick would coast to with plain friction, then the face nearest
  // to that point: the card keeps going the way it was thrown.
  GiftCard3D.prototype.releaseWithVelocity = function (k) {
    var predRy = this.ry + this.vy / (1 - FRICTION);
    var predRx = this.rx + this.vx / (1 - FRICTION);
    this.springTo(nearestFaceAngle(predRx, REST_RX), nearestFaceAngle(predRy, REST_RY), k);
  };

  // ---- pointer drag ----
  GiftCard3D.prototype.bindPointer = function () {
    var self = this;
    var card = this.card;

    card.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      self.dragging = true;
      self.mode = 'drag';
      self.classList.add('gc3d--dragging');
      self.pid = e.pointerId;
      self.touch = e.pointerType === 'touch';
      self.px = e.clientX;
      self.py = e.clientY;
      self.sx = e.clientX;
      self.sy = e.clientY;
      self.pt = performance.now();
      self.downAt = self.pt;
      self.vy = 0;
      self.vx = 0;
      self.moved = false;
      self.lightAt(e.clientX, e.clientY, 1);
      if (self.touch) self.haptic('touch');
      try { card.setPointerCapture(e.pointerId); } catch (err) { /* older Safari */ }
    });

    card.addEventListener('pointermove', function (e) {
      if (!self.dragging || e.pointerId !== self.pid) return;
      var dx = e.clientX - self.px;
      var dy = e.clientY - self.py;
      var now = performance.now();
      var dt = Math.max(1, now - self.pt);
      self.px = e.clientX;
      self.py = e.clientY;
      self.pt = now;
      if (Math.abs(e.clientX - self.sx) > TAP_MAX_PX || Math.abs(e.clientY - self.sy) > TAP_MAX_PX) self.moved = true;
      if (!self.moved) return;
      if (e.cancelable) e.preventDefault();
      self.lightAt(e.clientX, e.clientY, 1);
      // Sideways spins, up / down tumbles. Every swipe on the card is ours
      // (the touchmove handler below cancels the page scroll; Axel: a
      // vertical swipe must tilt the card, not scroll the page).
      // rotateX is the outer transform, so once the card has tumbled past
      // 90deg (upside down) a positive ry turns it the other way on screen;
      // mirror the sideways input there so a right swipe always turns right.
      var side = self.upsideSign();
      self.ry += dx * DRAG_SENS * side;
      self.rx -= dy * TILT_SENS;
      var maxV = self.touch ? MAX_V_TOUCH : MAX_V_MOUSE;
      self.vy = clamp((dx * DRAG_SENS * side) / dt * 16, -maxV, maxV);   // deg per 16ms frame
      self.vx = clamp((-dy * TILT_SENS) / dt * 16, -maxV, maxV);
    });

    var end = function (e, cancelled) {
      if (!self.dragging || (e && e.pointerId !== undefined && e.pointerId !== self.pid)) return;
      self.dragging = false;
      self.classList.remove('gc3d--dragging');
      try { card.releasePointerCapture(self.pid); } catch (err) { /* ignore */ }
      if (self.touch || !self.fine) self.lightOff();
      if (cancelled) {
        // The gesture was cancelled by the browser (a system gesture, a
        // second finger): no flip, no throw, just settle on the nearest face.
        self.vx = 0; self.vy = 0;
        self.releaseWithVelocity(K_FLIP);
        return;
      }
      var quick = performance.now() - self.downAt < TAP_MAX_MS;
      if (!self.moved && quick) {
        self.flip();
        return;
      }
      if (performance.now() - self.pt > 80) { self.vx = 0; self.vy = 0; }   // held still before letting go
      self.releaseWithVelocity(K_FLICK);
      if (self.touch && self.landed) self.haptic('throw');
    };
    card.addEventListener('pointerup', function (e) { end(e, false); });
    card.addEventListener('pointercancel', function (e) { end(e, true); });
    card.addEventListener('lostpointercapture', function (e) { end(e, true); });
    card.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // The card owns every swipe on it (up/down tumbles, sideways spins).
    // touch-action stays `manipulation` for the iOS haptic, so the page
    // scroll is cancelled here, from a non-passive listener.
    card.addEventListener('touchmove', function (e) { if (e.cancelable) e.preventDefault(); }, { passive: false });
    card.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.flip(); }
    });
  };

  // ---- hover lean (desktop only, while at rest) ----
  GiftCard3D.prototype.bindHover = function () {
    if (!this.fine || this.reduced) return;
    var self = this;
    var stage = this.querySelector('.gc3d__stage') || this;
    stage.addEventListener('mousemove', function (e) {
      if (self.dragging) return;
      var r = stage.getBoundingClientRect();
      self.hoverX = clamp((e.clientX - r.left) / r.width - 0.5, -0.5, 0.5);
      self.hoverY = clamp((e.clientY - r.top) / r.height - 0.5, -0.5, 0.5);
      self.lightAt(e.clientX, e.clientY, 0.85);
    });
    stage.addEventListener('mouseleave', function () {
      self.hoverX = 0;
      self.hoverY = 0;
      self.lightOff();
    });
  };

  // ---- device tilt (phones, opt-in: iOS asks permission inside the tap) ----
  GiftCard3D.prototype.bindTilt = function () {
    var btn = this.tiltBtn;
    if (!btn || this.reduced || !('DeviceOrientationEvent' in window)) return;
    var coarse = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!coarse) return;
    btn.hidden = false;
    var self = this;
    btn.addEventListener('click', function () {
      self.haptic(self.tilting ? 'off' : 'on');
      if (self.tilting) { self.stopTilt(); return; }
      var DOE = window.DeviceOrientationEvent;
      if (typeof DOE.requestPermission === 'function') {
        DOE.requestPermission().then(function (state) {
          if (state === 'granted') self.startTilt();
          else btn.hidden = true;
        }).catch(function () { btn.hidden = true; });
      } else {
        self.startTilt();
      }
    });
  };

  GiftCard3D.prototype.startTilt = function () {
    var self = this;
    this.tilting = true;
    this.tiltBase = null;
    this.classList.add('gc3d--tilting');
    if (this.tiltBtn) this.tiltBtn.setAttribute('aria-pressed', 'true');
    this.glintSide = 0;
    // The sensor fires up to 60 times a second; keep only the latest
    // reading and let the animation loop consume it once per frame.
    this.onTilt = function (e) {
      if (e.gamma == null || e.beta == null) return;
      if (!self.tiltBase) self.tiltBase = { g: e.gamma, b: e.beta };   // wherever the phone is held now is level
      self.tiltRead = { g: e.gamma, b: e.beta };
    };
    window.addEventListener('deviceorientation', this.onTilt, true);
  };

  GiftCard3D.prototype.applyTilt = function () {
    var r = this.tiltRead;
    if (!r || !this.tiltBase) return;
    this.tiltRead = null;
    var dg = clamp(r.g - this.tiltBase.g, -28, 28) / 28;          // left / right
    var db = clamp(r.b - this.tiltBase.b, -28, 28) / 28;          // towards / away
    this.hoverX = dg * 0.5;
    this.hoverY = db * 0.5;
    this.gx = 0.5 + dg * 0.45;
    this.gy = 0.5 + db * 0.45;
    this.glareT = 0.75;
    // The foil glints as the light crosses the disc (centre at 71.5% of
    // the width on the front face): one soft tick per crossing.
    if (!this.showingBack()) {
      var side = this.gx > 0.715 ? 1 : -1;
      if (this.glintSide && side !== this.glintSide) this.haptic('glint');
      this.glintSide = side;
    }
  };

  GiftCard3D.prototype.stopTilt = function () {
    this.tilting = false;
    this.tiltRead = null;
    this.classList.remove('gc3d--tilting');
    if (this.tiltBtn) this.tiltBtn.setAttribute('aria-pressed', 'false');
    if (this.onTilt) window.removeEventListener('deviceorientation', this.onTilt, true);
    this.hoverX = 0; this.hoverY = 0;
    this.lightOff();
  };

  // ---- haptics (touch devices only) ----
  // Android: navigator.vibrate, from anywhere. iOS Safari has no vibration
  // API; the only web haptic is the system click of a switch-style checkbox
  // (<input type="checkbox" switch>) and since iOS 26.5 it fires only for a
  // REAL tap, never for a scripted click. So, as in assets/skylit-wheel.js,
  // the card and the two buttons are wrapped in labels holding a hidden
  // switch: the user's own tap toggles it. Swipes do not click, so iPhone
  // ticks on taps only; Android also ticks on landing, edge-on and glint.
  function hiddenSwitch() {
    var sw = document.createElement('input');
    sw.type = 'checkbox';
    sw.setAttribute('switch', '');
    sw.tabIndex = -1;
    sw.setAttribute('aria-hidden', 'true');
    sw.style.cssText = 'position:absolute;width:1px;height:1px;margin:0;padding:0;visibility:hidden;pointer-events:none;';
    sw.addEventListener('click', function (e) { e.stopPropagation(); });   // the label's re-dispatched click stays private
    return sw;
  }

  GiftCard3D.prototype.bindHaptics = function () {
    var coarse = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!coarse) return;
    this.canVibrate = typeof navigator.vibrate === 'function';
    this.hapticsOn = this.canVibrate;
    this.lastHaptic = 0;
    if (this.canVibrate) return;
    var isIOS = /iP(hone|ad|od)/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!isIOS) return;
    // The card: a label around it, display: contents so layout and the 3D
    // scene are untouched.
    var wrap = document.createElement('label');
    wrap.className = 'gc3d__haptic-wrap';
    wrap.style.display = 'contents';
    wrap.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    this.card.parentNode.insertBefore(wrap, this.card);
    wrap.appendChild(this.card);
    wrap.appendChild(hiddenSwitch());
    // The buttons: a transparent label over each; the click still bubbles.
    [this.flipBtn, this.tiltBtn].forEach(function (btn) {
      if (!btn) return;
      var label = document.createElement('label');
      label.setAttribute('aria-hidden', 'true');
      label.style.cssText = 'position:absolute;inset:0;touch-action:manipulation;border-radius:inherit;';
      label.style.setProperty('-webkit-tap-highlight-color', 'transparent');
      label.appendChild(hiddenSwitch());
      if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
      btn.appendChild(label);
    });
  };

  // Android only (iOS ticks come from the labels above).
  GiftCard3D.prototype.haptic = function (kind) {
    if (!this.hapticsOn || !this.canVibrate) return;
    var now = performance.now();
    var gap = kind === 'glint' ? 260 : 90;
    if (now - this.lastHaptic < gap) return;
    this.lastHaptic = now;
    try { navigator.vibrate(kind === 'land' || kind === 'throw' ? 14 : kind === 'glint' ? 5 : 8); } catch (e) { /* never let feedback break the card */ }
  };

  // ---- flip: half a turn sideways, always the same way round ----
  GiftCard3D.prototype.flip = function () {
    this.vx = 0; this.vy = 0;
    var ty = nearestFaceAngle(this.ry, REST_RY) + 180;
    this.springTo(nearestFaceAngle(this.rx, REST_RX), ty, K_FLIP);
  };

  GiftCard3D.prototype.showingBack = function () {
    return Math.cos(this.ry * DEG) * Math.cos(this.rx * DEG) < 0;
  };
  // -1 while the card is upside down (tumbled past 90deg), else 1.
  GiftCard3D.prototype.upsideSign = function () {
    return Math.cos(this.rx * DEG) < 0 ? -1 : 1;
  };

  // ---- value swap ----
  GiftCard3D.prototype.setValue = function (text) {
    if (!this.valueEl || this.valueEl.textContent === text) return;
    var el = this.valueEl;
    el.textContent = text;
    this.card.setAttribute('aria-label', 'Skylit Studio ' + text + ' gift card');
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
    if (this.showingBack()) this.flip();
    else if (!this.reduced) {
      // A little wobble so the eye goes to the card.
      this.vy = 5;
      this.springTo(nearestFaceAngle(this.rx, REST_RX), nearestFaceAngle(this.ry, REST_RY), K_NUDGE);
    }
  };

  // ---- loop ----
  GiftCard3D.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    var self = this;
    var step = function (now) {
      if (!self.running) return;
      if (!self.visible) { self.running = false; return; }
      self.tick(now);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  GiftCard3D.prototype.tick = function (now) {
    var t = (now - this.t0) / 1000;
    if (this.tilting) this.applyTilt();

    if (this.mode === 'spring') {
      // Critically damped spring on both axes, carrying the release velocity.
      var c = 2 * Math.sqrt(this.k);
      this.vy = clamp(this.vy + this.k * (this.ty - this.ry) - c * this.vy, -30, 30);
      this.vx = clamp(this.vx + this.k * (this.tx - this.rx) - c * this.vx, -30, 30);
      this.ry += this.vy;
      this.rx += this.vx;
      if (Math.abs(this.ty - this.ry) < 0.12 && Math.abs(this.tx - this.rx) < 0.12 && Math.abs(this.vy) < 0.02 && Math.abs(this.vx) < 0.02) {
        this.ry = this.ty; this.rx = this.tx;
        // Keep the numbers small; the pose is the same.
        this.ry = mod(this.ry - REST_RY, 360) + REST_RY;
        this.rx = mod(this.rx - REST_RX, 360) + REST_RX;
        this.restRy = this.ry; this.restRx = this.rx;
        this.vx = 0; this.vy = 0;
        this.mode = 'rest';
        if (this.landed) this.haptic('land');
        this.landed = false;
      }
    } else if (this.mode === 'rest') {
      // A slow sway plus the hover / tilt lean.
      var sway = this.reduced ? 0 : Math.sin(t * 0.55) * 7;
      var bob = this.reduced ? 0 : Math.cos(t * 0.42) * 3.5;
      var targetRy = this.restRy + sway + this.hoverX * 26 * this.upsideSign();
      var targetRx = this.restRx + bob - this.hoverY * 20;
      this.ry += (targetRy - this.ry) * 0.06;
      this.rx += (targetRx - this.rx) * 0.08;
    }

    var cy = Math.cos(this.ry * DEG), sy = Math.sin(this.ry * DEG);
    var cx = Math.cos(this.rx * DEG), sx = Math.sin(this.rx * DEG);

    // Sheen slides with the visible face's angle; lift with the tilt.
    var faceAngle = mod(this.ry + 90, 180) - 90;          // -90..90 relative to a face
    // Fine steps: the idle sway moves this only a few percent, and whole
    // percents made it jump about once a second instead of gliding.
    var sheen = (18 + (faceAngle / 90) * 70).toFixed(2) + '%';
    var lift = (Math.abs(sx) * 6).toFixed(0) + 'px';
    // Which face and which edges point at the viewer (see the CSS for why
    // this is done here and not with backface-visibility).
    var back = cy * cx < 0;
    var upside = cx < 0;
    var edgeL = sy * cx > 0.02, edgeR = sy * cx < -0.02;
    var edgeT = sx < -0.02, edgeB = sx > 0.02;
    // Corner segments: outward normal (nx, ny) in card space, after
    // rotateY then rotateX its z is ny*sin(rx) - nx*sin(ry)*cos(rx).
    for (var i = 0; i < this.corners.length; i++) {
      var seg = this.corners[i];
      var on = (seg.ny * sx - seg.nx * sy * cx) > 0.02;
      if (on !== seg.on) { seg.el.classList.toggle('is-on', on); seg.on = on; }
    }

    // The light drifts after its target so it never snaps.
    this.cgx += (this.gx - this.cgx) * 0.14;
    this.cgy += (this.gy - this.cgy) * 0.14;
    this.glare += (this.glareT - this.glare) * (this.glareT > this.glare ? 0.12 : 0.05);
    // Upside down the faces are turned in their plane (gc3d--upside), so
    // mirror the light to keep it under the finger. Values are unitless
    // 0..1 and rounded to 2 decimals so a resting card writes nothing.
    var lx = upside ? 1 - this.cgx : this.cgx;
    var ly = upside ? 1 - this.cgy : this.cgy;
    var px = lx.toFixed(2);
    var py = ly.toFixed(2);
    var gl = this.glare.toFixed(2);
    // Foil slides with the light and with the turn. The idle sway moves it
    // only ~0.03 either way, so it is compared at the written precision
    // (hundredths of a percent); 2-decimal rounding made it step, not glide.
    var fx = clamp(lx + (faceAngle / 90) * 0.4, 0, 1);
    var fy = clamp(ly + sx * 0.3, 0, 1);
    var ft = 'translate(' + ((fx - 0.5) * 50).toFixed(2) + '%, ' + ((fy - 0.5) * 50).toFixed(2) + '%)';

    var L = this.last;
    var i;
    if (px !== L.px || py !== L.py) {
      var gt = 'translate(' + ((lx - 0.5) * 50).toFixed(1) + '%, ' + ((ly - 0.5) * 50).toFixed(1) + '%)';
      for (i = 0; i < this.glares.length; i++) this.glares[i].style.transform = gt;
      L.px = px; L.py = py;
    }
    if (ft !== L.ft && this.foilLight) {
      this.foilLight.style.transform = ft;
      L.ft = ft;
    }
    if (gl !== L.gl) {
      for (i = 0; i < this.glares.length; i++) this.glares[i].style.opacity = gl;
      if (this.foil) this.foil.style.opacity = (0.13 + this.glare * 0.3).toFixed(2);
      L.gl = gl;
    }
    if (back !== L.back) { this.classList.toggle('gc3d--back', back); if (L.back !== undefined) this.haptic('edge'); L.back = back; }
    if (upside !== L.upside) { this.classList.toggle('gc3d--upside', upside); L.upside = upside; }
    if (edgeL !== L.eL) { this.classList.toggle('gc3d--edge-l', edgeL); L.eL = edgeL; }
    if (edgeR !== L.eR) { this.classList.toggle('gc3d--edge-r', edgeR); L.eR = edgeR; }
    if (edgeT !== L.eT) { this.classList.toggle('gc3d--edge-t', edgeT); L.eT = edgeT; }
    if (edgeB !== L.eB) { this.classList.toggle('gc3d--edge-b', edgeB); L.eB = edgeB; }
    var rx = this.rx.toFixed(2) + 'deg';
    var ry = this.ry.toFixed(2) + 'deg';
    if (rx !== L.rx || ry !== L.ry || lift !== L.lift) {
      this.card.style.transform = 'translateY(-' + lift + ') rotateX(' + rx + ') rotateY(' + ry + ')';
      L.rx = rx; L.ry = ry; L.lift = lift;
    }
    if (sheen !== L.sheen) {
      for (i = 0; i < this.sheens.length; i++) {
        var back_ = this.sheens[i].parentNode.classList.contains('gc3d__face--back');
        this.sheens[i].style.transform = 'translateX(' + (back_ ? '-' : '') + sheen + ')';
      }
      L.sheen = sheen;
    }
  };

  customElements.define('gift-card-3d', GiftCard3D);
})();
